/**
 * Custom fetch wrapper to handle Node.js 20 undici content-length mismatch errors
 * This addresses the UND_ERR_REQ_CONTENT_LENGTH_MISMATCH error occurring with the back button navigation
 */

interface FetchError extends Error {
  code?: string;
  cause?: {
    code?: string;
  };
}

/**
 * Enhanced fetch function that handles content-length mismatch errors in Node.js 20
 * @param input - URL or Request object
 * @param init - RequestInit options
 * @returns Promise<Response>
 */
export const fetchWithRetry = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  // Get the original fetch function to avoid infinite recursion
  const fetchFn = (globalThis as any).__originalFetch || globalThis.fetch;

  try {
    return await fetchFn(input, init);
  } catch (error) {
    const fetchError = error as FetchError;

    // Check for Node.js 20 undici content-length mismatch error
    if (
      fetchError.code === 'UND_ERR_REQ_CONTENT_LENGTH_MISMATCH' ||
      fetchError.cause?.code === 'UND_ERR_REQ_CONTENT_LENGTH_MISMATCH' ||
      fetchError.message?.includes('Request body length does not match content-length header')
    ) {
      console.warn('Content-length mismatch detected, retrying without content-length header');

      // Retry without content-length header
      const retryInit = { ...init };
      if (retryInit.headers) {
        const headers = new Headers(retryInit.headers);
        headers.delete('content-length');
        headers.delete('Content-Length');
        retryInit.headers = headers;
      }

      return fetchFn(input, retryInit);
    }

    // Re-throw other errors
    throw error;
  }
};

/**
 * Helper function to calculate proper content length for a request body
 * @param body - Request body (string, FormData, Blob, etc.)
 * @returns number - Content length in bytes
 */
export const calculateContentLength = (body: unknown): number => {
  if (!body) return 0;

  if (typeof body === 'string') {
    return new TextEncoder().encode(body).length;
  }

  if (body instanceof Blob) {
    return body.size;
  }

  if (body instanceof ArrayBuffer) {
    return body.byteLength;
  }

  if (body instanceof FormData) {
    // FormData size calculation is complex, let the browser handle it
    return 0;
  }

  // For other types, convert to string and calculate
  try {
    return new TextEncoder().encode(JSON.stringify(body)).length;
  } catch {
    return 0;
  }
};

/**
 * Enhanced fetch with proper content-length handling
 * @param input - URL or Request object
 * @param init - RequestInit options
 * @returns Promise<Response>
 */
export const fetchWithProperContentLength = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
  const requestInit = { ...init };

  // Only set content-length for POST, PUT, PATCH requests with body
  if (requestInit.body && requestInit.method && ['POST', 'PUT', 'PATCH'].includes(requestInit.method.toUpperCase())) {
    const contentLength = calculateContentLength(requestInit.body);

    if (contentLength > 0) {
      const headers = new Headers(requestInit.headers);
      headers.set('content-length', contentLength.toString());
      requestInit.headers = headers;
    }
  }

  return fetchWithRetry(input, requestInit);
};

/**
 * Global fetch polyfill for Node.js 20 compatibility
 * This can be used to override the global fetch function
 */
export const setupGlobalFetchPolyfill = (): void => {
  if (typeof globalThis !== 'undefined' && globalThis.fetch) {
    // Store the original fetch before replacing it (only if not already stored)
    if (!(globalThis as any).__originalFetch) {
      (globalThis as any).__originalFetch = globalThis.fetch;
    }

    // Replace global fetch with our enhanced version
    globalThis.fetch = fetchWithRetry;
  }
};

/**
 * Restore original fetch function
 */
export const restoreOriginalFetch = (): void => {
  if (typeof globalThis !== 'undefined' && (globalThis as any).__originalFetch) {
    globalThis.fetch = (globalThis as any).__originalFetch;
    delete (globalThis as any).__originalFetch;
  }
};
